/**
 * Scheduler / timeline role tokens.
 *
 * Every column, cell and timeline block belongs to one of three roles. What a
 * role *looks* like depends on the active color theme, so the values here are
 * `var(--sched-*)` references rather than hex: the palettes live in
 * `src/styles/schedule/_scheduler-theme.scss`, keyed on
 * `<html data-scheduler-theme>`.
 *
 * Why `var()` strings rather than plain colors resolved in TypeScript: the
 * grids paint cells through an inline `[style.background]` binding, because a
 * `ColorRule` can override any group's fill with an arbitrary color. Keeping
 * the binding inline preserves the specificity the components already rely on
 * (it still beats `.assigned-on-machine` and the drag-over states), while the
 * value stays theme-reactive without the store, the selectors or the component
 * ever knowing which theme is active.
 *
 * - `rail` — a saturated leading edge on the first cell of a run of this
 *   role's columns. `transparent` in a theme that does not use rails.
 * - `fill` — the cell background.
 * - `ink`  — text on that fill.
 *
 * Two directions currently ship:
 * - `specs/design_handoff_scheduler_rail/README.md` (option 1b, the default)
 * - `specs/design_handoff_scheduler_cool_triad/README.md` (option 1c)
 */
export type SchedulerRoleId = 'tooling' | 'material' | 'job';

export interface SchedulerRole {
  /** Stable discriminator — compare roles on this, never by object identity. */
  id: SchedulerRoleId;
  /** Saturated leading edge on the first cell of a run. */
  rail: string;
  /** Cell background. */
  fill: string;
  /** Text on `fill`. */
  ink: string;
  /** Background for a collapsed summary row of this role. */
  summaryFill: string;
}

function role(id: SchedulerRoleId, token: string): SchedulerRole {
  return {
    id,
    rail: `var(--sched-${token}-rail)`,
    fill: `var(--sched-${token}-fill)`,
    ink: `var(--sched-${token}-ink)`,
    summaryFill: `var(--sched-${token}-summary-fill)`,
  };
}

export const TOOLING_ROLE = role('tooling', 'tooling');
export const MATERIAL_ROLE = role('material', 'material');
export const JOB_ROLE = role('job', 'job');

/**
 * The job card in the timeline job rows. The job role for every purpose that
 * matters — run detection, rail, ordering — but with its own ground, because
 * one direction deliberately leaves the card white while the other tints it.
 */
export const JOB_CARD_ROLE = role('job', 'jobcard');

/**
 * Neutrals that do not vary by theme.
 *
 * The state palette — past due, breakdown, no-coil, now-line — is deliberately
 * *not* here. Those colors are identical in both directions and are only ever
 * applied from stylesheets, so they live once, as Sass variables, in
 * `src/styles/schedule/jobs/jobs.scss` and the theme file. That is the point of
 * the design: the loud colors are the ones that mean something is wrong, and
 * they must not drift when someone switches direction.
 */
export const SCHEDULER_NEUTRALS = {
  /** Cell values on an untinted or rule-painted ground. */
  primaryText: '#1D1F20',
} as const;
